import { z } from 'zod'

/**
 * Payload that the backoffice encodes into a QR code immediately after a
 * tablet is approved. The tablet app scans this QR and has everything it
 * needs to start calling the API: its own UID (so it can verify the QR
 * matches its pending registration), the shared HMAC secret, and the API
 * base URL.
 *
 * Format is plain JSON. Payloads are ~250 bytes which comfortably fits a
 * 33×33 (medium-density) QR at error-correction level M. Version-tagged so
 * future changes can coexist with older tablet firmware.
 */
export const TABLET_PROVISIONING_VERSION = 1
export const TABLET_PROVISIONING_MAGIC = 'collector.tablet.provisioning'

export const TabletProvisioningPayloadSchema = z.object({
  v: z.literal(TABLET_PROVISIONING_VERSION),
  magic: z.literal(TABLET_PROVISIONING_MAGIC),
  tabletUid: z.string().min(8).max(128),
  secret: z.string().min(32),
  apiBaseUrl: z.string().url(),
})

export type TabletProvisioningPayload = z.infer<typeof TabletProvisioningPayloadSchema>

export interface BuildProvisioningInput {
  tabletUid: string
  secret: string
  apiBaseUrl: string
}

export function buildTabletProvisioningPayload(
  input: BuildProvisioningInput,
): TabletProvisioningPayload {
  return {
    v: TABLET_PROVISIONING_VERSION,
    magic: TABLET_PROVISIONING_MAGIC,
    tabletUid: input.tabletUid,
    secret: input.secret,
    apiBaseUrl: input.apiBaseUrl,
  }
}

export function encodeTabletProvisioning(input: BuildProvisioningInput): string {
  const payload = buildTabletProvisioningPayload(input)
  TabletProvisioningPayloadSchema.parse(payload)
  return JSON.stringify(payload)
}

export function decodeTabletProvisioning(raw: string): TabletProvisioningPayload {
  const parsed = JSON.parse(raw) as unknown
  return TabletProvisioningPayloadSchema.parse(parsed)
}
